import React, { FormEvent } from 'react'
import MediaQuery from 'react-responsive'

import { Bodytext, Button } from '@acorns/web-components'
import styled from 'styled-components'

import SelectInput from 'components/Form/SelectInput'
import FullscreenTakeover from 'components/FullscreenTakeover'
import { InvestmentAccountClosureReason } from 'generated/types'
import useWindowDimensions from 'hooks/useWindowDimensions'
import themeValues, { style } from 'theme'
import { PageAnalytics } from 'utils/analytics'

import { CloseAccountWrapper } from '../closeAccountWrapper'
import { StyledCard } from '../components'
import { enhance } from './enhance'

type IReason = {
  value: string
  label: string
}

type IProps = {
  data?: any
  exitWebViewCancel: () => void
  handleChange: (event: FormEvent<HTMLTextAreaElement>) => void
  handleReasonChange: (value: string) => void
  handleSubmit: () => void
  handleCancelSelect: () => void
  isValid: boolean
  isWebView: boolean
  reasons: InvestmentAccountClosureReason[]
  shuffledReasons: InvestmentAccountClosureReason[]
  showModal: boolean
  values: FormValues
}

const Section = styled.div`
  max-width: 355px;
  text-align: center;
  margin: 0 auto;
  padding-bottom: 30px;

  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 20px;
    padding-top: 40px;
    width: 100%;
  }
`

const TopSection = styled.div`
  padding-left: 50px;
  padding-right: 50px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    border-bottom: 1px solid ${style((theme) => theme.colors.ivory)};
    padding: 20px;
  }
`
const BottomSection = styled.div`
  max-width: 355px;
  margin: 0 auto;
  padding-bottom: 20px;
  background-color: ${style((theme) => theme.colors.white)};
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 20px;
    width: 100%;
  }
`
const Card = styled(StyledCard)<{ innerHeight?: number }>`
  text-align: left;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 0px;
    &&& {
      height: ${(props) => `calc(${props.innerHeight}px - 78px)`};
      justify-content: flex-start;
      overflow: scroll;
    }
  }
`
const WVCard = styled(StyledCard)<{ innerHeight?: number }>`
  text-align: left;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 0px;
    &&& {
      height: ${(props) => `calc(${props.innerHeight}px - 80px)`};
      justify-content: flex-start;
      overflow: scroll;
      border-radius: 0;
    }
  }
`

const Text = styled(Bodytext)`
  padding-left: 20px;
  padding-right: 20px;
  text-align: center;
  margin-bottom: 30px;
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    padding: 0px;
    margin-bottom: 0;
  }
`

const Actions = styled.div`
  width: 100%;
  background-color: ${style((theme) => theme.colors.white)};
  @media screen and (min-width: ${style((theme) => theme.breakpoints.sm)}px) {
    border-top: 1px solid ${style((theme) => theme.colors.ivory)};
    display: flex;
    justify-content: flex-end;
  }
  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    margin-top: auto;
    padding: 20px;
    width: 100%;
  }
`

const CloseButton = styled(Button)`
  &&& {
    min-width: 270px;
    margin: 0 auto;
    margin-top: 30px;
  }

  @media screen and (max-width: ${style(
      (theme) => theme.breakpoints.sm - 1,
    )}px) {
    width: 100%;
    margin-top: 0px;
  }
`

const CloseReasonDropdown = styled(SelectInput)`
  max-height: 45vh;
`

export const Page: React.FC<IProps> = (props) => {
  const CardComponent = props.isWebView ? WVCard : Card
  const { height } = useWindowDimensions()
  return (
    <>
      {props.isWebView && (
        <>
          {!props.data.loading && <PageAnalytics />}
          <FullscreenTakeover innerHeight={height}>
            <CloseAccountWrapper
              isWebView={props.isWebView}
              action={{
                onClick: props.handleCancelSelect,
                shouldShowCloseIcon: false,
              }}
              link={{
                onClick: props.handleCancelSelect,
                text: 'Cancel',
              }}
              actionStripTitle={'Close account'}
              showCancellable={true}
              handleCancel={props.handleCancelSelect}
            >
              <CardComponent isWebView={props.isWebView} innerHeight={height}>
                <MediaQuery maxWidth={themeValues.breakpoints.sm - 1}>
                  <TopSection>
                    <Text>
                      So we can make Acorns better, can you tell us why you want
                      to close your account(s)?
                    </Text>
                  </TopSection>
                </MediaQuery>
                <MediaQuery minWidth={themeValues.breakpoints.sm}>
                  <Section>
                    So we can make Acorns better, can you tell us why you want
                    to close your account(s)?
                  </Section>
                </MediaQuery>

                <BottomSection>
                  <CloseReasonDropdown
                    testId="reason"
                    label="Reason for closing your account(s)"
                    name="reason"
                    onChange={props.handleReasonChange}
                    options={props.shuffledReasons}
                    placeholder={'Select Reason'}
                    value={props.values.reason}
                    useNative={false}
                    hideOverlay={true}
                  />
                </BottomSection>
                <Actions>
                  <CloseButton
                    disabled={!props.isValid}
                    onPress={props.handleSubmit}
                  >
                    Next
                  </CloseButton>
                </Actions>
              </CardComponent>
            </CloseAccountWrapper>
          </FullscreenTakeover>
        </>
      )}
      {!props.isWebView && (
        <CloseAccountWrapper
          isWebView={props.isWebView}
          actionStripTitle="Reason for closing"
          actionText="Go back"
          backOverride={props.backOverride}
          showCancellable={true}
          handleCancel={props.handleCancelSelect}
        >
          <Card>
            <>
              <Section>
                So we can make Acorns better, can you tell us why you want to
                close your account(s)?
              </Section>

              <BottomSection>
                <CloseReasonDropdown
                  testId="reason"
                  label="Reason for canceling subscription"
                  name="reason"
                  onChange={props.handleReasonChange}
                  options={props.shuffledReasons}
                  placeholder={'Select Reason'}
                  value={props.values.reason}
                  useNative={false}
                />
              </BottomSection>
              <Actions>
                <CloseButton
                  disabled={!props.isValid}
                  onPress={props.handleSubmit}
                >
                  Next
                </CloseButton>
              </Actions>
            </>
          </Card>
        </CloseAccountWrapper>
      )}
    </>
  )
}

export default enhance(Page)
